"use client";

import { LocaleLink } from "@i18n/routing";
import { cn } from "@repo/ui";
import { useConsent } from "@shared/hooks/cookie-consent";
import { PrimaryCta } from "@repo/ui/components/design-system/cta/primary-cta";
import { SecondaryCta } from "@repo/ui/components/design-system/cta/secondary-cta";
import { Z_INDEX } from "@shared/lib/z-index";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

export function ConsentBanner() {
	const t = useTranslations("cookies.banner");
	const { bannerOpen, acceptAll, rejectNonEssential, open } = useConsent();
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	if (!mounted || !bannerOpen) {
		return null;
	}

	const shell = (
		<section
			aria-label={t("title")}
			className={cn(
				"pointer-events-none fixed inset-x-0 bottom-0 flex justify-center",
				"p-4 pb-[max(1rem,env(safe-area-inset-bottom))]",
			)}
			style={{ zIndex: Z_INDEX.consent.content }}
		>
			<div className="pointer-events-auto w-full max-w-2xl rounded-2xl border border-border bg-card p-4 text-card-foreground shadow-xl sm:p-6">
				<p className="text-sm leading-normal">{t("body")}</p>
				<p className="mt-2 text-sm">
					<LocaleLink
						className="text-primary underline-offset-4 hover:underline"
						href="/legal/cookie-policy"
					>
						{t("policyLink")}
					</LocaleLink>
				</p>
				<div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
					<SecondaryCta
						type="button"
						className="w-full sm:w-auto sm:min-w-[9rem]"
						ctaSize="small"
						onClick={() => rejectNonEssential()}
					>
						{t("rejectNonEssential")}
					</SecondaryCta>
					<SecondaryCta
						type="button"
						className="w-full sm:w-auto sm:min-w-[9rem]"
						ctaSize="small"
						onClick={() => open()}
					>
						{t("customize")}
					</SecondaryCta>
					<PrimaryCta
						type="button"
						className="w-full sm:w-auto sm:min-w-[9rem]"
						ctaSize="small"
						onClick={() => acceptAll()}
					>
						{t("acceptAll")}
					</PrimaryCta>
				</div>
			</div>
		</section>
	);

	return createPortal(shell, document.body);
}
